import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import MovieCard from '../components/MovieCard';
import { searchMovies, getPopularMovies } from '../utils/api';
import './Home.css';

const Home = () => {
    const [movies, setMovies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [searchParams] = useSearchParams();
    const observerTarget = useRef(null);

    const query = searchParams.get('s');

    useEffect(() => {
        document.title = query ? `Search: ${query} - MovieHub` : 'MovieHub - Popular Movies';
    }, [query]);

    // Initial fetch
    useEffect(() => {
        setMovies([]);
        setPage(1);
        setTotalResults(0);
        setError(null);
        setLoading(true);

        fetchMovies(query, 1, true);
    }, [query]);

    const fetchMovies = async (searchQuery, pageNum, isReset = false) => {
        try {
            setLoading(true);
            let data;

            if (searchQuery) {
                data = await searchMovies(searchQuery, pageNum);
            } else {
                data = await getPopularMovies(pageNum);
            }

            if (data.Search) {
                if (isReset) {
                    setMovies(data.Search);
                } else {
                    setMovies(prev => [...prev, ...data.Search]);
                }
                setTotalResults(parseInt(data.totalResults, 10));
            } else {
                if (isReset) setError(data.Error || 'No results found');
            }
        } catch (err) {
            if (isReset) setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        if (!loading && movies.length < totalResults) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchMovies(query, nextPage, false);
        }
    }, [loading, movies.length, totalResults, page, query]);

    // Infinite Scroll Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            { threshold: 1.0 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [loadMore]);

    return (
        <div className="home-page fade-in">
            <div className="container">
                <div className="hero-section">
                    <h1 className="page-title">
                        {query ? `Search Results for "${query}"` : 'Popular Movies'}
                    </h1>
                    <p className="page-subtitle">
                        {query
                            ? `Found ${totalResults} movies`
                            : 'Discover the latest and greatest movies'}
                    </p>
                </div>

                {error && (
                    <div className="error-container">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="error-icon">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        <h3>Oops! Something went wrong</h3>
                        <p>{error}</p>
                    </div>
                )}

                <div className="movies-grid">
                    {movies.map((movie, index) => (
                        <MovieCard key={`${movie.imdbID}-${index}`} movie={movie} />
                    ))}
                </div>

                {!error && (
                    <div ref={observerTarget} className="loader-container">
                        {loading && <div className="loader"></div>}
                        {!loading && movies.length > 0 && movies.length >= totalResults && (
                            <p className="end-message">You've reached the end!</p>
                        )}
                    </div>
                )}

                {!loading && movies.length === 0 && !error && (
                    <div className="empty-state">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <h3>No movies found</h3>
                        <p>Try searching for something else</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Home;
